import isEmpty from 'lodash-es/isEmpty'
import { usePathname, useSearchParams } from 'next/navigation'
import { ComponentProps, useCallback, useMemo, useRef } from 'react'

import { Clock, Media } from '@syconium/little-miss-figgy'

import { VariantSelectionColorRadioGroup } from '../../../../../app/_components/forms/VariantSelectionColorField'
import { VariantSelectionFitRadioGroup } from '../../../../../app/_components/forms/VariantSelectionFitField'
import {
  VariantSelectionForm,
  VariantSelectionFormPagesDirectoryQueryParamBinding,
  useVariantSelectionForm,
} from '../../../../../app/_components/forms/VariantSelectionForm'
import { VariantSelectionSizeRadioGroup } from '../../../../../app/_components/forms/VariantSelectionSizeField'
import { VariantSelectionFormPurchasingSubmit } from '../../../../../app/_components/forms/VariantSelectionSubmit'
import { usePortal } from '../../../../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { RefsContainer } from '../../../../../brunswick/containers/refs'
import { useAccentColor } from '../../../../../containers/AccentColorContainer'
import {
  EmbroideryService,
  useMakeEmbroideredCartItem,
} from '../../../../../containers/EmbroideryService'
import { EmbroideryModalService } from '../../../../../containers/EmbroideryService/EmbroideryModalService'
import { convertEmbroideryResponse } from '../../../../../containers/EmbroideryService/utils/unorganized'
import { useUpdateQueryParam } from '../../../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import { EmbroiderableProductFlatlayImage } from '../../../../../types/graphql'
import { EmbroideryModal } from '../../../../embroidery/EmbroideryModal/EmbroideryModal'
import { CompleteTheSet } from '../CompleteTheSet/CompleteTheSet'
import { usePortalEmbroiderySettings } from '../hooks/usePortalEmbroiderySettings'
import { useProductPageEmbroiderySettings } from '../hooks/useProductPageEmbroiderySettings'
import { usePdpDeprecatedServerSideProps } from '../PdpDeprecatedServerSidePropsProvider'
import { usePdpServerSideProps } from '../PdpServerSidePropsProvider'
import { ProductHighlights } from '../ProductHighlights/ProductHighlights'
import { DetailsSectionSidePaddedSection } from '../styles'
import WhyWeLove from '../WhyWeLoveSection/WhyWeLoveSection'

import { CollectionLinks } from './CollectionLink/CollectionLink'
import { EmbroiderySection } from './EmbroiderySection/EmbroiderySection'
import {
  EmbroiderySectionFormError,
  embroiderySectionFormFieldName,
} from './EmbroiderySection/EmbroiderySectionFormError'
import { ProductDetailsAccordionSection } from './ProductDetailsAccordionSection/ProductDetailsAccordionSection'
import { ProductDetailsPromoMessages } from './ProductDetailsPromoMessages'
import * as Styles from './StandardProductDetails.styles'
import { StickyAddToBagModal } from './StickyAddToBagModal'
import * as SharedStyles from './styles'

type PdpDeprecatedProductGroup = NonNullable<
  NonNullable<ReturnType<typeof usePdpDeprecatedServerSideProps>['data']>['productGroup']
>

type PdpDeprecatedFeaturedProduct = NonNullable<
  NonNullable<PdpDeprecatedProductGroup>['featuredProduct']
>

type StandardProductDetailsProps = {
  product: NonNullable<ReturnType<typeof usePdpServerSideProps>['data']>['product']
  variantGroup: NonNullable<
    ReturnType<typeof usePdpServerSideProps>['data']
  >['product']['variantGroup']
  /** @deprecated */
  deprecatedProps: {
    /** @deprecated */
    featuredProduct: PdpDeprecatedFeaturedProduct
    /** @deprecated */
    productGroup: PdpDeprecatedProductGroup
  }
}

export const StandardProductDetails = (props: StandardProductDetailsProps) => {
  return (
    <StandardProductDetailsProviders {...props}>
      <StandardProductDetailsImpl {...props} />
    </StandardProductDetailsProviders>
  )
}

const StandardProductDetailsProviders = ({
  variantGroup,
  product,
  deprecatedProps: {
    featuredProduct: deprecatedFeaturedProduct,
    productGroup: deprecatedProductGroup,
  },
  children,
}: StandardProductDetailsProps & { children?: React.ReactNode }) => {
  const { hasMandatoryLogoEmbroidery, hasMandatoryTextEmbroidery, portal } = usePortal()
  const mandatoryLogoEmbroidery = hasMandatoryLogoEmbroidery({
    categoryAnalyticsId: variantGroup.primaryCategory.analyticsId,
  })
  const mandatoryTextEmbroidery = hasMandatoryTextEmbroidery({
    categoryAnalyticsId: variantGroup.primaryCategory.analyticsId,
  })
  const {
    embroidery: { discountText },
  } = useTranslation()

  const defaultEmbroideryOptions = useMemo(() => {
    return convertEmbroideryResponse(deprecatedFeaturedProduct.embroidery)
  }, [deprecatedFeaturedProduct.embroidery])

  // Embroidery settings related to the current product/product group
  const { embroideryOptions } = useProductPageEmbroiderySettings({
    defaultEmbroideryOptions,
    mandatoryLogoEmbroidery,
    isPortalColorSelected: variantGroup.isPortalColor,
  })

  // The discount message to display at the bottom of the embroidery modal. Currently displayed only on portal-applicable PDP's.
  const embroideryDiscountMessage = useMemo(() => {
    const portalDiscountPercentage = portal?.embroidery?.embroideryDiscountPercentage

    // Only display portal discount messaging if the portal-specific logo embroidery option is present
    const hasPortalEmbroideryOptions = embroideryOptions.some(({ kind }) => kind === 'LOGO_ONLY')
    if (!portalDiscountPercentage || !hasPortalEmbroideryOptions) return undefined

    return discountText.formSection.replace(
      '{{discountPercentage}}',
      portalDiscountPercentage.toString()
    )
  }, [
    discountText.formSection,
    embroideryOptions,
    portal?.embroidery?.embroideryDiscountPercentage,
  ])

  // Embroidery settings related to the user's current portal
  const {
    hiddenEmbroideryFields,
    defaultInitialFormValues,
    defaultSelectedEmbroideryOptions,
    textEmbroiderySecondLineOptions,
    requiredEmbroideryOptions,
  } = usePortalEmbroiderySettings({
    embroideryOptions,
    mandatoryLogoEmbroidery,
    mandatoryTextEmbroidery,
    isPortalColorSelected: variantGroup.isPortalColor,
  })

  const productImagesWithDefaultFlatlays: EmbroiderableProductFlatlayImage[] = useMemo(() => {
    const baseImages: EmbroiderableProductFlatlayImage[] =
      deprecatedFeaturedProduct.images?.nodes ?? []
    return baseImages.concat(deprecatedProductGroup.embroideryImages ?? [])
  }, [deprecatedFeaturedProduct, deprecatedProductGroup])

  return (
    <EmbroideryService.Provider
      initialState={{
        hideFields: hiddenEmbroideryFields,
        defaultInitialFormValues,
        defaultSelectedEmbroideryOptions,
        embroideryOptions,
        productImagesWithDefaultFlatlays,
        productColor: variantGroup.color
          ? {
              hexCodes: variantGroup.color.hexCodes ?? [],
              name: variantGroup.color.displayValue,
              rawName: variantGroup.color.id,
            }
          : undefined,
        categoryAnalyticsId: variantGroup.primaryCategory.analyticsId,
        selectedSize: variantGroup.variant?.size?.id,
        textEmbroideryOptions: {
          secondLine: textEmbroiderySecondLineOptions,
        },
      }}
      key={`${product.handle}`} // It would be nice to remove this. I am not sure why we hard clear the DOM on product change. Ideally it could just naturally update.
    >
      <EmbroideryModalService.Provider
        initialState={{
          discountMessage: embroideryDiscountMessage,
          requiredEmbroideryOptions,
        }}
      >
        <EmbroideryModal />
        {children}
      </EmbroideryModalService.Provider>
    </EmbroideryService.Provider>
  )
}

type PdpColorOption = NonNullable<StandardProductDetailsProps['product']['options']['colors'][0]>

const StandardProductDetailsImpl = ({
  product,
  variantGroup,
  deprecatedProps: { featuredProduct: deprecatedFeaturedProduct },
}: StandardProductDetailsProps) => {
  const { accentColor } = useAccentColor()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { status } = usePdpServerSideProps()
  const { status: deprecatedStatus } = usePdpDeprecatedServerSideProps()
  const { portal } = usePortal()
  const isPortalColorSelected = variantGroup.isPortalColor
  const { detailsSectionAddToBagSectionRef } = RefsContainer.useContainer()
  const updateQueryParam = useUpdateQueryParam()
  const makeEmbroideredCartItem = useMakeEmbroideredCartItem()

  const {
    detailsSection: { portalPromoText },
    pages: { product: productPageStrings },
  } = useTranslation()

  // If the url lacks a size and there are more than one to pick from, don't chose for the user.
  const initialSizeId =
    isEmpty(searchParams?.get('size')) && product.options.sizes.length > 1
      ? null
      : variantGroup.variant?.size?.id

  const {
    formStore,
    queryVariables: { sizeFieldValue },
  } = useVariantSelectionForm({
    defaultValues: {
      color: variantGroup.color?.id,
      fit: variantGroup.variant?.fit?.id,
      size: initialSizeId,
      [embroiderySectionFormFieldName]: '',
    },
  })

  // Little weird, but we don't want to show this warning for the default size on load.
  // We also don't want to show it while pending from the default to the next.
  // Defer deciding until we are resolved with a loaded variant matching our field data.
  const displayLowStockWarningRef = useRef(false)
  if (status === 'resolved' && variantGroup.variant?.size?.id === sizeFieldValue) {
    displayLowStockWarningRef.current = variantGroup.variant?.stockState === 'LOW_STOCK'
  }

  const wwltImage = useMemo(
    () => deprecatedFeaturedProduct.images?.nodes.find(image => image.kind === 'wwlt'),
    [deprecatedFeaturedProduct.images?.nodes]
  )

  const [generalColorOptions, portalColorOptions] = useMemo(() => {
    const portalColorOptions = product.portalOptions?.colors ?? []
    const generalColorOptions = product.options.colors.filter(
      colorOption =>
        portalColorOptions.find(
          portalColorOption => portalColorOption.color.id === colorOption.color.id
        ) === undefined
    )
    return [generalColorOptions, portalColorOptions]
  }, [product.options.colors, product.portalOptions?.colors])

  // Filter out colors based on tab selection
  const variantSelectionFormProduct = useMemo(() => {
    return {
      ...product,
      options: {
        ...product.options,
        colors: isPortalColorSelected ? portalColorOptions : generalColorOptions,
      },
    }
  }, [generalColorOptions, isPortalColorSelected, portalColorOptions, product])

  // Generate the URL to use for a given tab
  // This will navigate the user to the current PDP url with the first color in the given color group selected
  const generateEmployeeStoreTabLink = useCallback(
    (colorOptions: PdpColorOption[]) => {
      const colorKey =
        colorOptions.find(colorOption => !colorOption.outOfStock)?.color?.id ??
        colorOptions[0]?.color?.id
      const search = new URLSearchParams(searchParams?.toString() ?? '')
      if (colorKey) search.set('color', colorKey)

      return {
        pathname,
        search: search.toString(),
      }
    },
    [searchParams, pathname]
  )

  // Add embroidery and portal info to the default add to cart logic.
  const makeCartItem = useCallback<
    NonNullable<ComponentProps<typeof VariantSelectionForm>['makeCartItem']>
  >(
    props => {
      return makeEmbroideredCartItem(
        {
          ...props,
          options: {
            ...props.options,
            attributedPortal:
              isPortalColorSelected && portal?.name
                ? {
                    name: portal.name,
                    itemType: 'PortalColor',
                  }
                : undefined,
          },
        },
        product.variantGroup.primaryCategory.analyticsId
      )
    },
    [
      isPortalColorSelected,
      makeEmbroideredCartItem,
      portal?.name,
      product.variantGroup.primaryCategory.analyticsId,
    ]
  )

  return (
    <div>
      {/* Pricing and badges */}
      <ProductHighlights />

      {/* Conditional portals tabs and content. */}
      {!isEmpty(portalColorOptions) ? (
        <Styles.TabContainer>
          {!isEmpty(generalColorOptions) && (
            <Styles.Tab
              aria-label='Show FIGS offerings'
              aria-current={!isPortalColorSelected}
              href={generateEmployeeStoreTabLink(generalColorOptions)}
              shallow={true}
              replace={true}
              scroll={false}
            >
              <Styles.TabText>FIGS</Styles.TabText>
            </Styles.Tab>
          )}
          <Styles.Tab
            aria-label='Show your organizations offerings'
            aria-current={isPortalColorSelected}
            href={generateEmployeeStoreTabLink(portalColorOptions)}
            shallow={true}
            replace={true}
            scroll={false}
          >
            <Styles.TabText>My Uniform</Styles.TabText>
          </Styles.Tab>
        </Styles.TabContainer>
      ) : null}
      {portal?.discountPercentage ? (
        <Styles.PortalPromoText data-testid='portal-promo-text'>{`${portal.discountPercentage}${portalPromoText}`}</Styles.PortalPromoText>
      ) : null}

      <VariantSelectionForm
        store={formStore}
        product={variantSelectionFormProduct}
        variantGroup={variantGroup}
        selectedVariant={variantGroup.variant}
        loading={status === 'pending' || deprecatedStatus === 'pending'}
        trackingCategory={'product details'}
        data-testid='pdp-variant-picker'
        isPrimaryFormForPageView
        makeCartItem={makeCartItem}
      >
        <VariantSelectionFormPagesDirectoryQueryParamBinding
          colorKey={'color'}
          fitKey={'fit'}
          sizeKey={'size'}
        />
        {/* Render the attribute selectors for the current product */}
        <DetailsSectionSidePaddedSection>
          <Styles.ProductDetailsVariantSelectionFormFields>
            <div>
              <VariantSelectionColorRadioGroup
                onChange={newValue => updateQueryParam('color', newValue)}
                colorGroupLabels={isEmpty(portalColorOptions) ? 'dynamic' : 'enforce'}
                priceBucketing={isPortalColorSelected ? 'disabled' : 'enabled'}
              />
              {displayLowStockWarningRef.current ? (
                <Styles.StandardProductDetailsLowInventoryTypography>
                  <Clock />
                  {productPageStrings.lowInventoryWarning}
                </Styles.StandardProductDetailsLowInventoryTypography>
              ) : null}
            </div>
            {/* <SizesSection /> */}
            <VariantSelectionSizeRadioGroup
              onChange={newValue => updateQueryParam('size', newValue)}
              sizeChart={product.sizeChart}
              callToActionColor={accentColor}
            />
            {/* <FitsSection /> */}
            <VariantSelectionFitRadioGroup
              onChange={newValue => updateQueryParam('fit', newValue)}
              callToActionColor={accentColor}
            />
          </Styles.ProductDetailsVariantSelectionFormFields>
        </DetailsSectionSidePaddedSection>

        <EmbroiderySection
          isPortalColorSelected={isPortalColorSelected}
          embroiderable={
            !isEmpty(deprecatedFeaturedProduct.embroidery) &&
            variantGroup.availability === 'AVAILABLE'
          }
        />
        <EmbroiderySectionFormError
          isPortalColorSelected={isPortalColorSelected}
          categoryAnalyticsId={variantGroup.primaryCategory.analyticsId}
        />

        {/* Add to bag button */}
        <SharedStyles.AddToBagButtonContainer ref={detailsSectionAddToBagSectionRef}>
          <VariantSelectionFormPurchasingSubmit callToActionColor={accentColor}>
            <Styles.ProductDetailsAddToBagContent />
          </VariantSelectionFormPurchasingSubmit>
          {variantGroup.color && variantGroup.variant?.fit ? (
            <Styles.PDPFavoritesButtonContainer>
              <Styles.PDPFavoritesButton
                productGroupHandle={product.handle}
                sizeKey={variantGroup.variant?.size?.id}
                colorKey={variantGroup.color.id}
                fitKey={variantGroup.variant?.fit.id}
                productGroupImageSrc={variantGroup.images?.[0]?.url}
                data-heap-category='Favorites (PDP)'
              />
            </Styles.PDPFavoritesButtonContainer>
          ) : null}
        </SharedStyles.AddToBagButtonContainer>
        <StickyAddToBagModal>
          <VariantSelectionFormPurchasingSubmit callToActionColor={accentColor}>
            <Styles.ProductDetailsAddToBagContent />
          </VariantSelectionFormPurchasingSubmit>
        </StickyAddToBagModal>
      </VariantSelectionForm>

      <ProductDetailsPromoMessages variantGroup={variantGroup} />

      <Media lessThan='md'>
        <Styles.WhyWeLoveDivider />
      </Media>
      <CompleteTheSet product={product} variantGroup={variantGroup} />

      {/* WWL mobile section */}
      <Media lessThan='md'>
        <Styles.WhyWeLoveDivider />
        <WhyWeLove
          imageSrc={wwltImage?.source}
          whyWeLoveText={variantGroup.whyWeLoveThis}
          whyWeLoveIcons={deprecatedFeaturedProduct.whyWeLoveIcons}
        />
      </Media>

      {/* WWL desktop section */}
      <Media greaterThanOrEqual='md'>
        <Styles.WhyWeLoveText>{variantGroup.whyWeLoveThis}</Styles.WhyWeLoveText>
      </Media>

      {/* In depth product details and info. */}
      <ProductDetailsAccordionSection
        careInstructions={variantGroup.careInstructions}
        fitModelsDescriptions={variantGroup.fitModelsDescriptions}
        features={variantGroup.features}
        materials={variantGroup.materials}
      />

      {/* Pill style links to associated collections. */}
      {variantGroup.associatedCollectionLinks ? (
        <CollectionLinks links={variantGroup.associatedCollectionLinks} />
      ) : null}
    </div>
  )
}
